const API_BASE = process.env.REACT_APP_FLASK_WEBSERVER;
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const APP_ID = process.env.REACT_APP_GOOGLE_APP_ID;

export class GoogleDriveNotConnectedError extends Error {
  constructor() {
    super('Google Drive is not connected');
    this.name = 'GoogleDriveNotConnectedError';
  }
}

const waitFor = (predicate, { timeout = 10000, interval = 100 } = {}) =>
  new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (predicate()) return resolve();
      if (Date.now() - start > timeout) return reject(new Error('Timed out waiting for Google scripts'));
      setTimeout(tick, interval);
    };
    tick();
  });

const loadPicker = () =>
  new Promise((resolve, reject) => {
    if (window.google && window.google.picker) return resolve();
    if (!window.gapi) return reject(new Error('gapi not loaded'));
    window.gapi.load('picker', { callback: resolve, onerror: reject });
  });

const fetchAccessToken = async (firebaseUser) => {
  const idToken = await firebaseUser.getIdToken();
  const res = await fetch(`${API_BASE}oauth/google/token`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (res.status === 401) throw new GoogleDriveNotConnectedError();
  if (!res.ok) throw new Error(`Failed to get Google access token (${res.status})`);
  const data = await res.json();
  return data.access_token;
};

export async function openGoogleDrivePicker({ user, onPick, onCancel }) {
  if (!API_KEY || !APP_ID) {
    throw new Error('Google Picker is not configured (missing REACT_APP_GOOGLE_API_KEY / REACT_APP_GOOGLE_APP_ID)');
  }
  if (!user) throw new Error('No authenticated user');

  const accessToken = await fetchAccessToken(user);

  await waitFor(() => !!window.gapi);
  await loadPicker();

  const { google } = window;
  const view = new google.picker.View(google.picker.ViewId.DOCS);

  const picker = new google.picker.PickerBuilder()
    .addView(view)
    .setOAuthToken(accessToken)
    .setDeveloperKey(API_KEY)
    .setAppId(APP_ID)
    .setCallback((data) => {
      if (data.action === google.picker.Action.PICKED) {
        const doc = data.docs && data.docs[0];
        if (doc && onPick) {
          onPick({
            link: doc[google.picker.Document.URL] || doc.url,
            name: doc[google.picker.Document.NAME] || doc.name,
            id: doc[google.picker.Document.ID] || doc.id,
            mimeType: doc[google.picker.Document.MIME_TYPE] || doc.mimeType,
          });
        }
      } else if (data.action === google.picker.Action.CANCEL && onCancel) {
        onCancel();
      }
    })
    .build();

  picker.setVisible(true);

  // The Picker iframe can land on Google's generic 403 page (e.g. if the
  // token doesn't have Drive scope). That page has no close button of its
  // own, so without these listeners the user gets trapped behind a modal
  // they can't dismiss. Allow Esc and a click on the dark backdrop to close.
  const dispose = () => {
    try {
      picker.setVisible(false);
      if (typeof picker.dispose === 'function') picker.dispose();
    } catch (_) {}
    document.removeEventListener('keydown', onKey, true);
    document.removeEventListener('mousedown', onBackdrop, true);
    if (onCancel) onCancel();
  };
  const onKey = (e) => {
    if (e.key === 'Escape') dispose();
  };
  const onBackdrop = (e) => {
    // .picker-dialog-bg is the dark overlay Google injects behind the iframe.
    if (e.target && e.target.classList && e.target.classList.contains('picker-dialog-bg')) {
      dispose();
    }
  };
  document.addEventListener('keydown', onKey, true);
  document.addEventListener('mousedown', onBackdrop, true);
}
